import { ChangeEvent, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { FEATURE_SHIPPING_APPS_RESTRICTIONS } from 'App/features';
import { ErrorScreen, Stack } from 'commons/components';
import { SHIPPING_CONFIGURE_RESTRICTIONS } from 'config/upsellFlowSources';
import { useGetCurrency } from 'domains/Auth/hooks';
import { useCategories } from 'domains/Catalog/Categories/hooks';
import { useGetProvinces } from 'domains/Shipping/Addresses/hooks';
import { SelectedZonesInterface } from 'domains/Shipping/components/shared/ZoneManagement/DeliveryZoneCard/DeliveryZoneCard';
import { useLocations } from 'domains/Shipping/Locations/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import Skeleton from './Skeleton';
import {
  useGetShippingRestrictions,
  useShippingMethodsOptions,
  type UpsellProtectionConfig,
} from '../../../../hooks';
import ShippingDisponibilityCard from '../../../components/ShippingDisponibilityCard';
import ShippingMethodsScopeCard from '../../../components/ShippingMethodsScopeCard';
import ShippingRestrictionsCard from '../../../components/ShippingRestrictionsCard';
import useGetDetailActiveApp from '../../../EditCarriersPage/useGetDetailActiveApp';
import { ShippingRestrictionFormValues } from '../../types';

interface ShippingRestrictionFormProps {
  values: ShippingRestrictionFormValues;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onArrayChange: (name: string, value: unknown[]) => void;
  errors?: Partial<Record<keyof ShippingRestrictionFormValues, string>>;
  currentRestrictionId?: string;
  isErrorDetail?: boolean;
  onRetryDetail?: () => void;
}

function ShippingRestrictionForm({
  values,
  onChange,
  onArrayChange,
  errors = {},
  currentRestrictionId,
  isErrorDetail = false,
  onRetryDetail,
}: Readonly<ShippingRestrictionFormProps>): JSX.Element {
  const { id: carrierId } = useParams<{ id: string }>();
  const t = useTranslationShipping('deliveryMethods.shippingLimiting');
  const { symbol } = useGetCurrency();

  const {
    detailActiveApp,
    fetchDetailActiveApp,
    isLoading: isLoadingApp,
    isError: isErrorApp,
  } = useGetDetailActiveApp();

  const { methods, isLoading: isLoadingMethods } =
    useShippingMethodsOptions(carrierId);

  const {
    restrictions,
    isLoading: isLoadingRestrictions,
    isError: isErrorRestrictions,
    fetchRestrictions,
  } = useGetShippingRestrictions();

  const {
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
    fetchCategoryOptions,
  } = useCategories();

  const {
    fetchLocations,
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
  } = useLocations();

  const {
    fetchProvinces,
    isLoading: isLoadingProvinces,
    isError: isErrorProvinces,
  } = useGetProvinces();

  const upsellConfig: UpsellProtectionConfig = useMemo(
    () => ({
      enabled: true,
      featureKey: FEATURE_SHIPPING_APPS_RESTRICTIONS,
      trackingSource: SHIPPING_CONFIGURE_RESTRICTIONS,
      title: t('configureRestrictionUpsell'),
    }),
    [t],
  );

  useEffect(() => {
    fetchDetailActiveApp(carrierId);
    fetchRestrictions(carrierId);
    fetchCategoryOptions();
    fetchLocations();
    fetchProvinces();
  }, [
    fetchDetailActiveApp,
    fetchRestrictions,
    fetchCategoryOptions,
    fetchLocations,
    fetchProvinces,
    carrierId,
  ]);

  const isLoading =
    isLoadingApp ||
    isLoadingMethods ||
    isLoadingRestrictions ||
    isLoadingCategories ||
    isLoadingLocations ||
    isLoadingProvinces;
  const hasError =
    isErrorApp ||
    isErrorCategories ||
    isErrorLocations ||
    isErrorRestrictions ||
    isErrorProvinces ||
    isErrorDetail;

  if (isLoading) {
    return <Skeleton />;
  }

  if (hasError) {
    return (
      <ErrorScreen
        description={t('genericError.text')}
        onRetry={() => {
          if (isErrorApp) fetchDetailActiveApp(carrierId);
          if (isErrorCategories) fetchCategoryOptions();
          if (isErrorLocations) fetchLocations();
          if (isErrorProvinces) fetchProvinces();
          if (isErrorRestrictions) fetchRestrictions(carrierId);
          if (isErrorDetail && onRetryDetail) onRetryDetail();
        }}
      />
    );
  }

  return (
    <Stack column align="stretch" spacing="base">
      <ShippingMethodsScopeCard
        appName={detailActiveApp?.name}
        methods={methods}
        restrictions={restrictions || []}
        currentRestrictionId={currentRestrictionId}
        value={values.shippingMethodsScope}
        onChange={onChange}
        selectedMethods={values.selectedShippingMethods || []}
        onMethodsChange={(methods) =>
          onArrayChange('selectedShippingMethods', methods)
        }
        upsellConfig={upsellConfig}
      />

      <ShippingDisponibilityCard
        distributionCenter={values.distributionCenterScope}
        deliveryZones={values.deliveryZonesScope}
        onChange={onChange}
        specificDistributionCenters={values.specificDistributionCenters}
        selectedZones={values.selectedZones}
        distanceInKm={values.deliveryDistanceInKm}
        onDistributionCentersChange={(centers) =>
          onArrayChange('specificDistributionCenters', centers)
        }
        onDeliveryZonesChange={(zones: SelectedZonesInterface[]) =>
          onArrayChange('selectedZones', zones)
        }
        errors={{
          distanceInKm: errors.deliveryDistanceInKm,
        }}
        upsellConfig={upsellConfig}
      />

      <ShippingRestrictionsCard
        restrictedProducts={values.productRestrictionType}
        cartWeight={values.weightRestrictionType}
        cartValue={values.valueRestrictionType}
        onChange={onChange}
        restrictedCategories={values.restrictedCategories}
        restrictedProductIds={values.restrictedProducts}
        onCategoriesChange={(categories) =>
          onArrayChange('restrictedCategories', categories)
        }
        onProductsChange={(products) =>
          onArrayChange('restrictedProducts', products)
        }
        cartWeightMin={values.cartWeightMin}
        cartWeightMax={values.cartWeightMax}
        cartValueMin={values.cartValueMin}
        cartValueMax={values.cartValueMax}
        errors={errors}
        currencySymbol={symbol}
        upsellConfig={upsellConfig}
      />
    </Stack>
  );
}

ShippingRestrictionForm.Skeleton = Skeleton;

export default ShippingRestrictionForm;
